// src/Pages/Patient.js
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from "react-router-dom";
import { KeycloakContext } from "../../context/KeycloakContext";
import { Button } from "primereact/button";
import { TabView, TabPanel } from 'primereact/tabview';
import VisualStrategy from "./Tabs/VisualStrategy";
import PupilBehavior from "./Tabs/PupilBehavior";
import {
    getSingleDataMeasurement,
    updateReferenceName,
    markAsReadyForOrdering,
    getUserDetails,
    getMeasurementPdf
} from "../../helpers/ApiRequests"
import { InputText } from "primereact/inputtext";
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import { useClickOutside } from 'primereact/hooks';
import {FlashingBorder} from "../../Shared/FlashingBorder"; // Importar useMediaQuery desde react-responsive


// Definition of a functional component
const Patient = ({ id, isLastReading = false }) => {
    const { kc ,theme, appInsights} = useContext(KeycloakContext);
    const { t, i18n } = useTranslation();
    const { patientId } = useParams();
    const [patientData, setPatientData] = useState({});
    const [referenceName, setReferenceName] = useState('');
    const [measurementId, setMeasurementId] = useState(id ?? patientId);
    const [originalReferenceName, setOriginalReferenceName] = useState(''); // Track original reference name
    const inputRef = useRef(null);
    const penRef = useRef(null);
    const tooltipRef = useRef(null);
    const [inputReadOnly, setInputReadOnly] = useState(true);
    const toast = useRef(null);
    const overlayRef = useRef(null);
    const navigate = useNavigate();
    const disabledBtnRef = useRef(null);
    const [isVisible, setIsVisible] = useState(true);

    const [inputWidth, setInputWidth] = useState(400); // Ancho inicial
    const spanRef = useRef(null);

    const getSingleDataPatient = async () => {
        const patient = await getSingleDataMeasurement(kc, measurementId);
        setPatientData(patient);
        setReferenceName(patient.ReferenceName);
        setOriginalReferenceName(patient.ReferenceName);
    };

    const handleSaveReferenceName = async () => {
        if (referenceName !== originalReferenceName) {
            await updateReferenceName(kc, measurementId, referenceName, toast, t);
            setOriginalReferenceName(referenceName); // Update original to the new saved value
        }
    };


    const handleMarkAsReadyForOrdering = async () => {
        if (referenceName) {
            await markAsReadyForOrdering(kc, measurementId, toast, t);
        } else {
            tooltipRef.current?.show({currentTarget: penRef.current});
        }
    };


    const goBack = () => {
        navigate("/");
    }

    useClickOutside(overlayRef, () => {
        handleSaveReferenceName()
    });

    useEffect(() => {
        getSingleDataPatient();
    }, []);

    useEffect(() => {
        // Actualiza el ancho del input basado en el span
        if (spanRef.current) {
            setInputWidth(spanRef.current.offsetWidth + 10); // +10 es un pequeño padding para evitar corte de texto
        }
    }, [referenceName]);


    const handleAnimationEnd = () => {
        setIsVisible(false);
    };

    const downloadPdf = async () => {
        console.log("Download PDF");

        if (appInsights) {
            console.log("Generate PDF insight");
            appInsights.trackEvent({ name: "Generate PDF" });
        }
        try {

          // 1) Fetch the PDF blob from the API
          const pdfBlob = await getMeasurementPdf(measurementId, theme, i18n.language);

          // 2) Create a URL for the blob
          const pdfUrl = window.URL.createObjectURL(pdfBlob);

          // 3) Create an <a> element, trigger a click to download
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.download = "measurement_report.pdf";

          // Append to DOM so Firefox can see it
          document.body.appendChild(link);
          link.click();

          // Cleanup
          link.remove();
          window.URL.revokeObjectURL(pdfUrl);
        } catch (error) {
          console.error("Error downloading PDF:", error);
          // Optionally show a toast or error message
          toast.current?.show({
            severity: 'error',
            summary: t('Error'),
            detail: t('Failed to download PDF.'),
          });
        }
      };

    const rawDeviceId = patientData?.processed_data?.data_origin_device_id;
    const headsetIdLast4 = rawDeviceId == null ? '' : String(rawDeviceId).slice(-4);
    //const isSaveAndSendDisabled = !patientData?.processed_data?.VisualBehaviourBasedDesignParams;
    const vbd = patientData?.processed_data?.VisualBehaviourBasedDesignParams;
    const isSaveAndSendDisabled =
        !vbd ||
        typeof vbd.F !== 'number' ||
        typeof vbd.I !== 'number' ||
        typeof vbd.N !== 'number' ||
        (vbd.F + vbd.I + vbd.N) !== 99;
    const hasLoadedPatient = !!patientData?.processed_data;



    return (
        <div className='container-page mx-auto relative z-1 p-1 overflow-y-hidden overflow-x-hidden'>
            <div className="container-flash-border border-1 border-white pt-3 pr-2 pl-2 pb-0 sm:pt-4 sm:pr-4 sm:pl-4 overflow-auto">
                {isVisible && (
                    <>
                        <FlashingBorder flash={'flash-border'} onAnimationEnd={handleAnimationEnd} />
                    </>
                )}
                <Toast ref={toast} />
                {isLastReading ? (
                    <div className="text-left text-xl my-1 mr-3 lg:my-2 lg:text-3xl text-white uppercase">
                        {t('lastReading')}
                    </div>
                ) : (
                    <div className="text-left text-xl my-1 mr-3 lg:my-2 lg:text-3xl text-white uppercase">
                    </div>
                )}
                <div className="flex flex-wrap sm:flex justify-content-between">
                    <div className="flex md:flex lg:flex flex-wrap justify-content-start items-start">
                        <div className="lg:flex lg:items-center">
                            <div className="flex-column text-left mb-4 md:mb-3 lg:mb-0" ref={overlayRef}>
                                <Tooltip
                                    className="mimetika-tooltip"
                                    content={t('modify-reference')}
                                    target={penRef}
                                    ref={tooltipRef}
                                />
                                <li onClick={ goBack } className="text-md lg:text-xl cursor-pointer mr-2 sm:mr-4 pi pi-arrow-left color-primary" />
                                <InputText
                                    ref={penRef}
                                    className="input-mimetika-3 text-left text-xl pl-1 mt-1 mr-3 lg:mt-0 lg:text-3xl text-white max-w-64 min-w-32 md:max-w-96 md:min-w-52 lg:max-w-2xl lg:min-w-96 sm:mb-2 lg:mb-0"
                                    value={referenceName ?? patientData.ReferenceName ?? ''}
                                    onChange={(e) => setReferenceName(e.target.value)}
                                    placeholder={t('Reference')}
                                    data-pr-position="left"
                                    data-pr-at="left+2 bottom+12"
                                    data-pr-my="left center-2"
                                    style={{ width: inputWidth }}
                                />
                                <span
                                    ref={spanRef}
                                    className="text-xl lg:text-3xl"
                                    style={{
                                        visibility: 'hidden',
                                        position: 'absolute',
                                        whiteSpace: 'pre',
                                        fontSize: 'inherit',
                                        fontFamily: 'inherit'
                                    }}>
                                {referenceName || "a"} {/* El valor dentro del span */}
                            </span>
                            </div>
                            <div className="mx-2 md:mr-0 my-0 text-left">
                                {/* Wrap the button with a div or span that handles the tooltip */}
                                <div ref={disabledBtnRef} style={{ display: 'inline-block' }}>
                                    <Button
                                        className={`btn-1 p-button-secondary`}
                                        onClick={ handleMarkAsReadyForOrdering }
                                        disabled={isSaveAndSendDisabled}
                                    >
                                        {t('MarkAsReadyForOrdering')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap justify-content-end relative sm:mr-32 md:mr-32 lg:mr-32 xl:mr-32 2xl:mr-72">
                        <div className=" text-left">
                            <div className="grid gap-0 m-0 filter-container text-white pt-2">
                                <div className="text-sm mr-4 my-1 text-left sm:my-0 lg:my-0"> {t('MeasurementUUID')}: {measurementId} </div>
                                <div className="text-sm mr-4 my-1 sm:my-0 lg:mt-2"> {t('date: ')} {new Date(patientData.timestamp).toLocaleString(i18n.language)}  </div>
                                <div className="text-sm mr-4 my-1 sm:my-0 lg:my-2"> {t('headset-id')}: {headsetIdLast4} </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card patient-tabs relative">
                    <div className=" lg:absolute z-5 top-0 right-0 pt-2">
                        <div className="flex flex-wrap text-white">
                            <div className="ml-4">
                                <Button className="btn-2" onClick={ downloadPdf }>{t('DownloadReport')}</Button>
                            </div>
                        </div>
                    </div>
                    {hasLoadedPatient && isSaveAndSendDisabled ? (
                            <div className="w-full text-center py-6">
                                <div className="text-3xl font-bold">
                                    {t('Error')}
                                </div>
                                <div className="text-3xl mt-4">
                                    {t('RepeatTest')}
                                </div>
                            </div>
                        ) : (
                            <TabView className="font-montserrat">
                                {patientData?.processed_data?.eyeMovementFactor && (
                                    <TabPanel header={t('VisualStrategy')}>
                                        <VisualStrategy data={patientData} />
                                    </TabPanel>
                                )}

                                {patientData?.processed_data?.eyetracking_available && 
                                 patientData?.processed_data?.processing_valid?.pupil_diameter_mm?.left &&
                                 patientData?.processed_data?.processing_valid?.pupil_diameter_mm?.right &&
                                (
                                    <TabPanel header={t('PupilBehavior')}>
                                        <PupilBehavior data={patientData} />
                                    </TabPanel>
                                )}
                            </TabView>
                        )}
                </div>
            </div>
        </div>
    );
}

export default Patient;
